import { isValid } from 'date-fns'
import { ExternalLink } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatDateTime } from '@/lib/utils/date'
import type { DraftSource } from '@/lib/api/types'

// Match the local pattern used in draft-sidebar.tsx / prd-draft-sidebar.tsx
// — guard against malformed timestamps without throwing.
function safeFormatDateTime(value: string | null | undefined): string | null {
  if (!value) return null
  const d = new Date(value)
  return isValid(d) ? formatDateTime(value) : null
}

interface DraftSourcesCardProps {
  sources: DraftSource[] | undefined
}

/**
 * Phase 30 (v4.3) — Web Source Provenance body card.
 *
 * Renders the web URLs the agent actually fetched and consumed during
 * generation. Per CONTEXT.md D-11 + AGENTS.md spec: when sources is
 * undefined or empty, the card hides entirely (returns null) — no
 * "No sources" copy. Mounted in the BODY column of the draft detail
 * page (not the sidebar) per D-05; the sidebar pointer block (separate
 * component) navigates to the `id="sources"` anchor on this card.
 */
export function DraftSourcesCard({ sources }: DraftSourcesCardProps) {
  if (!sources || sources.length === 0) return null

  return (
    <Card id="sources" className="scroll-mt-4 mt-6">
      <CardHeader>
        <CardTitle className="text-lg font-medium">Sources</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y divide-border">
          {sources.map((source, idx) => {
            const fetched = safeFormatDateTime(source.fetchedAt)
            return (
              <li
                key={`${source.url}-${idx}`}
                className="py-2 px-1 flex flex-col gap-1"
              >
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${source.title} (opens in new tab)`}
                  className="text-sm font-medium text-foreground hover:underline inline-flex items-center gap-1"
                >
                  {source.title}
                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                </a>
                <span className="text-xs text-muted-foreground truncate">
                  {source.url}
                </span>
                {fetched && (
                  <span className="text-xs text-muted-foreground">
                    Fetched {fetched}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
